import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { OrganisationFeatures, OrganisationService } from './organisation.service';
import { ScriptService } from './script.service';
import { StateCleanupService } from './state-cleanup.service';
import { UsageService } from './usage.service';
import { environment } from '../../environments/environment';

/** `userIds` entry that carries the portal access token to the websocket
 *  (al-tutor `app/portal_token.py` takes it out again before anything else
 *  reads the list). */
const PORTAL_TOKEN_ID_NAME = 'alphabeesPortalToken';

/** Renew the "Try me" preview token when less than this is left (Konten-PRD V6). */
const PREVIEW_RENEW_BEFORE_MS = 2 * 60 * 1000;

/** Read by the chat widget at connect time (its `learner-session.ts`). */
declare global {
  interface Window {
    alphabeesLearnerSession?: {
      accessToken(): string | null | undefined;
      ensureFresh?(): Promise<unknown>;
      onAuthFailure?(reason: string): void;
    };
  }
}

interface TryMeToken {
  access_token: string;
  expires_in: number;
}

export enum AlWindowEvent {
  ChatReady = 'alChatReady',
  UpdateState = 'alUpdateState',
  Reset = 'alReset',
  Connect = 'alConnect',
  State = 'alState',
  GetState = 'alGetState',
}

@Injectable({
  providedIn: 'root',
})
export class ChatPreviewService {
  isChatActive$ = new BehaviorSubject<boolean>(false);
  isChatReady$ = new BehaviorSubject<boolean>(false);
  /** The preview identity of this portal user for the agent being tried. */
  private previewToken?: { botId: string; token: string; expiresAt: number };

  constructor(
    protected scriptService: ScriptService,
    protected usageService: UsageService,
    protected stateCleanupService: StateCleanupService,
    protected authService: AuthService,
    protected organisationService: OrganisationService,
    protected http: HttpClient
  ) {
    this.startChatScript();
    this.stateCleanupService.register(() => this.resetState());
  }

  startChatScript() {
    return new Promise((resolve) => {
      this.scriptService
        .load('chatWidget')
        .then(() => {
          (window as any)._loadAlChat({
            apiKey: undefined,
            botId: undefined,
            isVisible: false,
            isOpen: false,
          });
        })
        .finally(() => {
          resolve(true);
        });
    });
  }

  connect(apiKey: string | undefined, botId: string, startOptions: any = {}) {
    if (!apiKey) {
      apiKey = this.usageService.apiKeys$.value?.[0]?.clear_api_key;
    }

    if (!botId || !apiKey) {
      console.error('apiKey or botId not set');
      return;
    }

    // Learning Engine: "Try me" chats as this portal user's preview learner
    // with a preview token — the widget sends it, the server takes client,
    // learner and agent from it (Konten-PRD V6). Not for the Data Analyst,
    // which reads the portal user's client from the portal token.
    if (
      this.organisationService.hasFeature(OrganisationFeatures.LEARNING_ENGINE) &&
      botId !== environment.analyticsBotId
    ) {
      this.connectAsPreviewLearner(apiKey, botId, startOptions);
      return;
    }

    const portalContext = this.buildPortalLearnerContext(startOptions);
    // The Data Analyst reads the analytics of the client this user is signed
    // in to. The backend takes that client from the access token, not from
    // an id the browser names — so the token goes along, for this bot only.
    const accessToken = this.authService.accessToken$.value;
    if (botId === environment.analyticsBotId && accessToken) {
      portalContext.additionalUserIds.push({
        id: accessToken,
        name: PORTAL_TOKEN_ID_NAME,
      });
    }
    const data = {
      apiKey,
      botId,
      isVisible: true,
      isChatOpen: true,
      ...startOptions,
      portalTestMode: true,
      chatSessionId: '',
      additionalUserIds: portalContext.additionalUserIds,
      userProfile: portalContext.userProfile,
    };

    window.postMessage(
      {
        type: AlWindowEvent.UpdateState,
        data,
      },
      '*'
    );

    window.postMessage(
      {
        type: AlWindowEvent.Connect,
      },
      '*'
    );
    this.isChatActive$.next(true);
  }

  /** V6: the preview token first; without one the old path (portal test mode). */
  private async connectAsPreviewLearner(apiKey: string, botId: string, startOptions: any) {
    try {
      await this.ensurePreviewToken(botId);
    } catch (err) {
      console.warn('Try me without preview identity (token not issued)', err);
      this.previewToken = undefined;
      delete window.alphabeesLearnerSession;
      this.connectLegacy(apiKey, botId, startOptions);
      return;
    }
    window.alphabeesLearnerSession = {
      accessToken: () => this.previewToken?.token,
      ensureFresh: () => this.ensurePreviewToken(botId),
      onAuthFailure: (reason: string) => {
        console.warn('Try me preview session ended:', reason);
        this.previewToken = undefined;
      },
    };
    const { additionalUserIds, userProfile, portalTestMode, ...rest } = startOptions || {};
    this.postConnect({
      apiKey,
      botId,
      isVisible: true,
      isChatOpen: true,
      ...rest,
      chatSessionId: '',
    });
  }

  private async ensurePreviewToken(botId: string): Promise<void> {
    const current = this.previewToken;
    if (current && current.botId === botId && current.expiresAt - Date.now() > PREVIEW_RENEW_BEFORE_MS) {
      return;
    }
    const issued = await firstValueFrom(
      this.http.post<TryMeToken>(`${environment.userBackend}/learner-area/preview/try-me`, { bot_id: botId })
    );
    this.previewToken = {
      botId,
      token: issued.access_token,
      expiresAt: Date.now() + issued.expires_in * 1000,
    };
  }

  private connectLegacy(apiKey: string, botId: string, startOptions: any) {
    const portalContext = this.buildPortalLearnerContext(startOptions);
    this.postConnect({
      apiKey,
      botId,
      isVisible: true,
      isChatOpen: true,
      ...startOptions,
      portalTestMode: true,
      chatSessionId: '',
      additionalUserIds: portalContext.additionalUserIds,
      userProfile: portalContext.userProfile,
    });
  }

  private postConnect(data: any) {
    window.postMessage({ type: AlWindowEvent.UpdateState, data }, '*');
    window.postMessage({ type: AlWindowEvent.Connect }, '*');
    this.isChatActive$.next(true);
  }

  closeChat() {
    delete window.alphabeesLearnerSession;
    window.postMessage(
      {
        type: AlWindowEvent.Reset,
      },
      '*'
    );

    window.postMessage(
      {
        type: AlWindowEvent.UpdateState,
        data: {
          isChatOpen: false,
          isVisible: false,
        },
      },
      '*'
    );

    this.isChatReady$.next(false);
    this.isChatActive$.next(false);
  }

  private resetState() {
    this.closeChat();
  }

  private buildPortalLearnerContext(startOptions: any) {
    const payload = this.authService.accessTokenPayload$.value;
    const organisation = this.organisationService.organisation;
    const existingIds = Array.isArray(startOptions?.additionalUserIds)
      ? startOptions.additionalUserIds
      : [];
    const additionalUserIds = [...existingIds];

    if (payload?.user_id) {
      additionalUserIds.push({
        id: payload.user_id,
        name: 'alphabeesUserId',
      });
    }

    const clientId = payload?.client_id || organisation?.id;
    if (clientId) {
      additionalUserIds.push({
        id: clientId,
        name: 'alphabeesClientId',
      });
    }

    const userProfile = {
      ...(startOptions?.userProfile || {}),
      displayName: payload?.name || startOptions?.userProfile?.displayName || '',
      email: payload?.email || startOptions?.userProfile?.email || '',
    };

    return {
      additionalUserIds: this.uniqueUserIds(additionalUserIds),
      userProfile,
    };
  }

  private uniqueUserIds(ids: Array<{ id?: string; name?: string }>) {
    const seen = new Set<string>();
    return ids
      .map((item) => ({
        id: String(item?.id || '').trim(),
        name: String(item?.name || '').trim(),
      }))
      .filter((item) => {
        if (!item.id || !item.name) return false;
        const key = `${item.name}:${item.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }
}
